import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import {
  Button, Menu,
} from '@mui/material';
import { UnfoldMore } from '@mui/icons-material/';
import PropTypes from 'prop-types';
import CustomTable from '../../components/table/CustomTable';
import { sendRequest } from '../../api/shootAPI';
import API from '../../api/endpoints';
import { UserContext } from '../../contexts/User';
import MemberNameEmailColumn from '../../components/table/MemberNameEmailColumn';
import { formatDate } from '../../utils/time';
import MultiSelect from '../../components/muiCustomized/MultiSelect';
import ViewIssueModal from './ViewIssueModal';
import useDebounce from '../../hooks/useDebounce';

const TABLE_HEADERS = [
  {
    label: 'Employee Name',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'name',
    colSpan: 1,
  },
  {
    label: 'Engagement Score',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'engagementScore',
    colSpan: 0.5,
  },
  {
    label: 'Hours Worked Today',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'hoursWorked',
    colSpan: 0.5,
  },
  {
    label: 'Idle Time',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'idleTime',
    colSpan: 0.5,
  },
  {
    label: 'Alerts Triggered',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'alertsTriggered',
    colSpan: 0.5,
  },
  {
    label: 'Date',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'createdAt',
    colSpan: 0.5,
  },
  {
    label: 'Action',
    colSpan: 0.5,
  },
];

const CustomTableStyles = {
  container: {
    marginTop: 1,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '14px',
    '.MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg) !important',
    },
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    button: {
      textWrap: 'nowrap',
      color: 'var(--dark-2)',
      borderColor: 'var(--dark-4) !important',
      '&:disabled': {
        color: 'var(--dark-5)',
        borderColor: 'var(--dark-7) !important',
        cursor: 'not-allowed',
        pointerEvents: 'all !important',
      },
    },
  },
};

const AlertTypes = [
  { id: 0, name: 'All' },
  { id: 1, name: 'HR' },
  { id: 2, name: 'IT' },
  { id: 3, name: 'Infosec' },
  { id: 4, name: 'Other' },
];

const Status = [
  { id: 0, name: 'All' },
  { id: 1, name: 'Acknowledged' },
  { id: 2, name: 'Pending' },
  { id: 3, name: 'Resolved' },
  { id: 4, name: 'Error' },
];

// eslint-disable-next-line react/prop-types
function BotAlerts({ category }) {
  const { search } = useLocation();
  const { activeWorkspace } = useContext(UserContext);
  const query = new URLSearchParams(search);
  const page = query.get('page');
  const history = useHistory();

  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [alerts, setAlerts] = useState([]);
  const { debounce } = useDebounce();
  const [sort, setSort] = useState(false);
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(page ? parseInt(page, 10) : 1);
  const [members, setMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([{ id: 0, name: 'All' }]);
  const [selectedAlertTypes, setSelectedAlertTypes] = useState([{ id: 0, name: 'All' }]);
  const [selectedStatus, setSelectedStatus] = useState([{ id: 0, name: 'All' }]);
  // const [selectedManagers, setSelectedManagers] = useState([{id: 0, name: 'All'}]);
  // const [date, setDate] = useState([new Date(moment(today).subtract(6, 'days')), today]);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const onClickView = (alert) => {
    setSelectedAlert(alert);
    setIsOpenModal(true);
  };

  const handleClose = () => {
    setIsOpenModal(false);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    history.push(`?page=${page}`);
  };

  const getAlerts = async (params) => {
    setIsLoading(true);
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAlerts(activeWorkspace.id),
      params: {
        pageSize: 10,
        alertType: [category],
        sortKey,
        sortOrder,
        ...params,
      },
    });
    if (response.success) {
      const mappedAlerts = response.data?.alerts.map((alert) => {
        const totalSeconds = alert?.member?.dailyActivity[0]?.totalSeconds || 0;
        const activeSeconds = alert?.member?.dailyActivity[0]?.activeSeconds || 0;
        const engagementScore = totalSeconds === 0 ? 0
          : Math.round((activeSeconds * 100) / totalSeconds);
        const idleTime = Math.round((totalSeconds - activeSeconds) / 3600);
        const hoursWorked = Math.round(totalSeconds / 3600);

        return {
          user: <MemberNameEmailColumn
            firstName={alert?.member?.user?.firstName}
            lastName={alert?.member?.user?.lastName}
            email={alert?.member?.user?.email}
            id={alert?.member?.id}
            component="div"
          />,
          engagementScore: `${engagementScore}%`,
          hoursWorked,
          idleTime,
          alertsTriggered: alert?.alertType.triggerCondition,
          date: formatDate(alert.createdAt),
          actions: (
            <Button
              type="button"
              className="inactive-status"
              onClick={() => onClickView(alert)}
              variant="outlined"
            >
              View Issue
            </Button>
          ),
        };
      });
      setAlerts(mappedAlerts);
      setIsLoading(false);
      setTotalPages(response.data?.totalPages ?? 1);
    } else {
      // handle API errors
    }
  };

  const getAllMembers = async () => {
    const response = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.members(activeWorkspace.id),
    });
    if (response.success) {
      const mappedmembers = response.data?.members.map((member) => ({
        id: member.id,
        name: member.user?.fullName,
      }));
      setMembers([{ id: 0, name: 'All' }, ...mappedmembers]);
    } else {
      // handle API errors
    }
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterMemberChange = (newValue) => {
    setSelectedMembers(newValue);
  };

  const handleFilterAlertTypeChange = (newValue) => {
    setSelectedAlertTypes(newValue);
  };

  const handleFilterStatusChange = (newValue) => {
    setSelectedStatus(newValue);
  };

  const handleReset = () => {
    setSelectedMembers([{ id: 0, name: 'All' }]);
    setSelectedAlertTypes([{ id: 0, name: 'All' }]);
    setSelectedStatus([{ id: 0, name: 'All' }]);
  };

  const handleSearch = () => {
    const memberIds = selectedMembers.map((item) => item.id);
    const status = selectedStatus.map((item) => item.name);
    const alertType = selectedAlertTypes.map((item) => item.name);
    const params = {
      memberIds: memberIds[0] === 0 ? null : memberIds,
      status: status[0] === 'All' ? null : status,
      alertType: alertType[0] === 'All' ? null : alertType,
      page: currentPage,
    };
    getAlerts(params);
  };

  const handleMarkAsAcknowledged = async () => {
    setIsUpdating(true);
    const response = await sendRequest({
      method: 'put',
      url: API.WORKSPACE.acknowledgedByManager(selectedAlert?.id),
    });
    setIsUpdating(false);
    if (response.success) {
      setIsOpenModal(false);
      getAlerts({ page: currentPage });
    } else {
      // handle API errors
    }
  };

  useEffect(() => {
    getAlerts({ page: currentPage });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, sort]);

  useEffect(() => {
    getAllMembers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSort = debounce((sortKey, sortOrder) => {
    setIsLoading(true);
    setSort(!sort);
    setSortKey(sortKey);
    setSortOrder(sortOrder);
  }, 250);

  return (
    <>
      <div className="py-2 px-3 d-flex justify-content-between align-items-center">
        <div>
          <h4 className="size-s text-600 lh-base fc-dark mb-1">
            HR Bot
          </h4>
          <p className="size-xs text-400 lh-sm fc-dark">
            Employee Engagement Monitoring
          </p>
        </div>
        <button className="filter-btn" type="button" onClick={handleFilterClick}>Filter & Search</button>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          sx={{
            '& .MuiMenu-list': {
              padding: 0,
            },
          }}
        >
          <div className="filter-panel p-3">
            <div className="filter-label mb-1">Employee Name</div>
            <MultiSelect options={members} value={selectedMembers} handleChange={handleFilterMemberChange} />
            <div className="filter-label mb-1 mt-2">Alert Type</div>
            <MultiSelect
              options={AlertTypes}
              value={selectedAlertTypes}
              handleChange={handleFilterAlertTypeChange}
            />
            <div className="filter-label mb-1 mt-2">Status</div>
            <MultiSelect options={Status} value={selectedStatus} handleChange={handleFilterStatusChange} />
            {/* <div className="filter-label my-1">Manager Name</div>
            <MultiSelect options={members} value={selectedMembers} /> */}
            {/* <div className="filter-label my-1">Date</div>
            <MultiSelect options={members} value={selectedMembers} /> */}
            <div className="d-flex justify-content-end gap-3 mt-2">
              <button
                type="button"
                className="filter-reset-btn"
                onClick={() => handleReset()}
              >
                Reset
              </button>
              <button type="button" className="filter-search-btn" onClick={() => handleSearch()}>Search</button>
            </div>
          </div>
        </Menu>
      </div>
      <div>
        <CustomTable
          headers={TABLE_HEADERS}
          items={alerts}
          handleSort={handleSort}
          loading={isLoading}
          pages={totalPages}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          styles={CustomTableStyles}
        />
      </div>
      <ViewIssueModal
        selectedAlert={selectedAlert}
        isOpen={isOpenModal}
        handleClose={handleClose}
        handleMarkAsAcknowledged={handleMarkAsAcknowledged}
        isUpdating={isUpdating}
      />
    </>
  );
}

BotAlerts.propTypes = {
  category: PropTypes.string.isRequired,
};

export default BotAlerts;
